'use client';

import { useState, useEffect, useMemo, useCallback } from 'react';
import { usePathname, useParams } from 'next/navigation';

import { IconX } from '@tabler/icons-react';

import { RichText } from '@/elements/rich-text/rich-text.component';
import { useAuth } from '@/providers/auth.provider';
import { useBanner } from '@/providers/banner.provider';

import { Container, MessageRow, CountdownRow, CloseButton } from './banner.elements';
import { pluralizeDan } from '@/helpers/pluralize.helpers';

const DISMISSED_BANNERS_KEY = 'dismissed_banners';
const DISMISS_EXPIRY_HOURS = 24;

/**
 * Get dismissed banners from localStorage
 */
const getDismissedBanners = () => {
  if (typeof window === 'undefined') return {};

  try {
    const stored = localStorage.getItem(DISMISSED_BANNERS_KEY);
    if (!stored) return {};

    const parsed = JSON.parse(stored);
    const now = Date.now();

    // Clean up expired dismissals
    const cleaned = Object.entries(parsed).reduce((acc, [id, expiry]) => {
      if (expiry > now) {
        acc[id] = expiry;
      }
      return acc;
    }, {});

    localStorage.setItem(DISMISSED_BANNERS_KEY, JSON.stringify(cleaned));
    return cleaned;
  } catch {
    return {};
  }
};

/**
 * Mark a banner as dismissed
 */
const dismissBanner = (bannerId) => {
  if (typeof window === 'undefined') return;

  try {
    const dismissed = getDismissedBanners();
    // Always use string IDs for consistency
    dismissed[String(bannerId)] = Date.now() + DISMISS_EXPIRY_HOURS * 60 * 60 * 1000;
    localStorage.setItem(DISMISSED_BANNERS_KEY, JSON.stringify(dismissed));
  } catch {
    // Ignore localStorage errors
  }
};

/**
 * Check if a banner matches the current URL placement
 */
const matchesPlacement = (banner, pathname, educationSlug) => {
  const { placement } = banner;

  if (!placement) return true;

  const { pages, educations, customPaths } = placement;
  const hasPages = pages && pages.length > 0;
  const hasEducations = educations && educations.length > 0;
  const hasCustomPaths = customPaths && customPaths.trim().length > 0;

  // If no placement restrictions, show everywhere
  if (!hasPages && !hasEducations && !hasCustomPaths) return true;

  // Check education-specific placement first (higher precedence)
  if (hasEducations && educationSlug) {
    const educationSlugs = educations.map((edu) => (typeof edu === 'object' ? edu.slug : edu));
    if (educationSlugs.includes(educationSlug)) {
      return { match: true, isEducationPlacement: true };
    }
  }

  // Check page placement
  if (hasPages) {
    const pageMatches = pages.some((page) => {
      switch (page) {
        case 'homepage':
          return pathname === '/';
        case 'educations':
          return pathname === '/edukacije' || pathname.startsWith('/edukacija/');
        case 'my-profile':
          return pathname === '/moj-profil' || pathname.startsWith('/moj-profil/');
        default:
          return false;
      }
    });

    if (pageMatches) {
      return { match: true, isEducationPlacement: false };
    }
  }

  // Check custom paths placement
  if (hasCustomPaths) {
    const paths = customPaths
      .split(',')
      .map((p) => p.trim().toLowerCase())
      .filter(Boolean);

    // Get first segment of pathname (e.g., /predavaci/john -> predavaci)
    const pathnameSegment = pathname.split('/')[1]?.toLowerCase() || '';

    if (paths.includes(pathnameSegment)) {
      return { match: true, isEducationPlacement: false };
    }
  }

  return false;
};

/**
 * Check if a banner matches the target audience
 */
const matchesAudience = (banner, user) => {
  const { targetAudience } = banner;

  switch (targetAudience) {
    case 'all':
      return true;
    case 'authenticated':
      return !!user;
    case 'unauthenticated':
      return !user;
    case 'subscribers':
      return user?.subscriptionStartedAt != null;
    case 'non-subscribers':
      return !user?.subscriptionStartedAt;
    default:
      return true;
  }
};

/**
 * Check if a banner is within its active date range
 */
const isWithinDateRange = (banner) => {
  const now = new Date();

  // Check start date
  if (banner.startDate && new Date(banner.startDate) > now) {
    return false;
  }

  // Check deadline - hide banner after deadline passes
  if (banner.deadline && new Date(banner.deadline) < now) {
    return false;
  }

  return true;
};

/**
 * Filter and select the best banner to display
 */
const selectBanner = (banners, pathname, educationSlug, user, dismissedIds) => {
  // Filter active banners
  const activeBanners = banners.filter((banner) => {
    if (!banner.isActive) return false;
    // Always compare as strings for consistency
    if (dismissedIds.includes(String(banner.id))) return false;
    if (!isWithinDateRange(banner)) return false;
    if (!matchesAudience(banner, user)) return false;

    const placementResult = matchesPlacement(banner, pathname, educationSlug);
    if (!placementResult) return false;

    // Attach placement info for precedence sorting
    banner._isEducationPlacement =
      typeof placementResult === 'object' ? placementResult.isEducationPlacement : false;

    return true;
  });

  if (activeBanners.length === 0) return null;

  // Sort by: education placement first, then priority (descending)
  activeBanners.sort((a, b) => {
    // Education placements take precedence
    if (a._isEducationPlacement && !b._isEducationPlacement) return -1;
    if (!a._isEducationPlacement && b._isEducationPlacement) return 1;

    // Then sort by priority
    return (b.priority || 0) - (a.priority || 0);
  });

  // Get banners with the highest priority (after sorting)
  const highestPriority = activeBanners[0].priority || 0;
  const topBanners = activeBanners.filter(
    (b) =>
      (b.priority || 0) === highestPriority &&
      b._isEducationPlacement === activeBanners[0]._isEducationPlacement
  );

  // Randomize if multiple banners have the same priority
  if (topBanners.length > 1) {
    return topBanners[Math.floor(Math.random() * topBanners.length)];
  }

  return topBanners[0];
};

/**
 * Calculate countdown text from deadline
 */
const getCountdownText = (deadline) => {
  if (!deadline) return null;

  const now = new Date();
  const end = new Date(deadline);
  const diff = end - now;

  // Banner will be hidden when expired, so this shouldn't happen
  if (diff <= 0) return null;

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  const seconds = Math.floor((diff % (1000 * 60)) / 1000);

  if (days > 0) {
    return `Još ${days} ${pluralizeDan(days)}!`;
  }

  const paddedHours = String(hours).padStart(2, '0');
  const paddedMinutes = String(minutes).padStart(2, '0');
  const paddedSeconds = String(seconds).padStart(2, '0');

  return `Još ${paddedHours}:${paddedMinutes}:${paddedSeconds}!`;
};

/**
 * Get the best active discount from education plans
 */
const getActiveDiscount = (education) => {
  const educationPlans = education?.plans?.educationPlans;
  if (!educationPlans?.length) return null;

  const now = new Date();

  // Find all plans with active discounts
  const activeDiscounts = educationPlans
    .filter((plan) => {
      // First check if the plan itself is active (from/to dates)
      const hasActiveFrom = plan.activeFrom && plan.activeFrom !== '';
      const hasActiveTo = plan.activeTo && plan.activeTo !== '';

      // If neither is set, plan is always active
      if (hasActiveFrom || hasActiveTo) {
        const fromCheck = !hasActiveFrom || new Date(plan.activeFrom) <= now;
        const toCheck = !hasActiveTo || new Date(plan.activeTo) >= now;

        if (!fromCheck || !toCheck) return false;
      }

      // Then check discount conditions
      const discount = plan.discount || 0;
      const discountExpiresAt = plan.discountExpiresAt;

      if (discount <= 0) return false;
      if (!discountExpiresAt) return false; // Require expiration for banner
      return new Date(discountExpiresAt) > now;
    })
    .map((plan) => ({
      discount: plan.discount,
      expiresAt: plan.discountExpiresAt,
    }));

  if (activeDiscounts.length === 0) return null;

  // Return the one with the highest discount (or earliest expiry if same)
  activeDiscounts.sort((a, b) => {
    if (b.discount !== a.discount) return b.discount - a.discount;
    return new Date(a.expiresAt) - new Date(b.expiresAt);
  });

  return activeDiscounts[0];
};

const Banner = ({ banners = [] }) => {
  const pathname = usePathname();
  const params = useParams();
  const { user } = useAuth();
  const { education } = useBanner();

  // Start with empty array to avoid hydration mismatch, then load from localStorage
  const [dismissedIds, setDismissedIds] = useState([]);
  const [countdown, setCountdown] = useState(null);
  const [isClient, setIsClient] = useState(false);

  // Get education slug from URL params
  const educationSlug = params?.slug;

  // Load dismissed banners from localStorage after client-side hydration
  useEffect(() => {
    setIsClient(true);
    const dismissed = getDismissedBanners();
    const ids = Object.keys(dismissed);
    setDismissedIds(ids);
  }, []);

  // Get active discount from education if available
  const activeDiscount = useMemo(() => {
    if (!education) return null;
    return getActiveDiscount(education);
  }, [education]);

  // Create a discount banner object if there's an active discount
  const discountBanner = useMemo(() => {
    if (!activeDiscount) return null;

    return {
      id: `discount-${education?.id}`,
      style: 'light-rose',
      message: null, // We'll render custom text instead
      discountText: `Iskoristi ${activeDiscount.discount}% popusta i unaprijedi svoje znanje uz edukaciju koja te vodi do stvarnih rezultata!`,
      deadline: activeDiscount.expiresAt,
      isDismissable: false,
      isDiscountBanner: true,
    };
  }, [activeDiscount, education?.id]);

  // Select the best banner to display
  const selectedCMSBanner = useMemo(() => {
    return selectBanner(banners, pathname, educationSlug, user, dismissedIds);
  }, [banners, pathname, educationSlug, user, dismissedIds]);

  // Determine final banner with precedence:
  // 1. CMS education-specific banner (highest)
  // 2. Discount banner
  // 3. General CMS banners
  const selectedBanner = useMemo(() => {
    // If CMS banner is education-specific, it takes highest precedence
    if (selectedCMSBanner?._isEducationPlacement) {
      return selectedCMSBanner;
    }

    // Check if discount banner is dismissed (compare as strings)
    if (discountBanner && !dismissedIds.includes(String(discountBanner.id))) {
      return discountBanner;
    }

    // Fall back to general CMS banner
    return selectedCMSBanner;
  }, [selectedCMSBanner, discountBanner, dismissedIds]);

  // Update countdown every minute (or every second if less than an hour)
  useEffect(() => {
    if (!selectedBanner?.deadline) {
      setCountdown(null);
      return;
    }

    const updateCountdown = () => {
      setCountdown(getCountdownText(selectedBanner.deadline));
    };

    updateCountdown();

    // Determine update interval based on remaining time
    const diff = new Date(selectedBanner.deadline) - new Date();
    // Update every second when showing time, every minute when showing days
    const interval = diff < 24 * 60 * 60 * 1000 ? 1000 : 60000;

    const timer = setInterval(updateCountdown, interval);
    return () => clearInterval(timer);
  }, [selectedBanner?.deadline]);

  const handleDismiss = useCallback(() => {
    if (selectedBanner) {
      dismissBanner(selectedBanner.id);
      setDismissedIds((prev) => [...prev, String(selectedBanner.id)]);
    }
  }, [selectedBanner]);

  // Don't render anything until client-side hydration is complete
  if (!isClient) return null;

  if (!selectedBanner) return null;

  return (
    <Container $style={selectedBanner.style}>
      <MessageRow>
        {selectedBanner.isDiscountBanner ? (
          <span>{selectedBanner.discountText}</span>
        ) : (
          <RichText content={selectedBanner.message} />
        )}
        {countdown && <CountdownRow>{countdown}</CountdownRow>}
      </MessageRow>

      {selectedBanner.isDismissable && (
        <CloseButton onClick={handleDismiss} aria-label="Zatvori banner">
          <IconX size={18} />
        </CloseButton>
      )}
    </Container>
  );
};

export { Banner };
